/*
 * Yutrients per-ingredient PDP — client-side controller.
 *
 * Two behaviours:
 *
 *   1. fetchCatalogueAndHydrate()
 *      GET /apps/yutrients/catalogue, find the ingredient by slug
 *      (slug = product.handle = data-yt-slug on section root), then
 *      inject long-form prose into [data-yt-prose="..."] containers
 *      and render the RELATED INGREDIENTS scroller.
 *
 *      The fetch result is cached in sessionStorage for 5 minutes —
 *      subsequent ingredient PDP visits in the same session re-use
 *      the same catalogue payload.
 *
 *   2. addToBlend() — ADD TO MY FORMULA button
 *      Mutates BYO sessionStorage under yutrients_byo_state and
 *      redirects to /pages/build-your-own. BYO reads this key on
 *      load and pre-hydrates the picker, so the customer lands
 *      with the ingredient already selected at its default dose.
 *
 *      We don't call /cart/add.js here — the BYO is the unit of
 *      purchase, not the individual ingredient. Per the spec, every
 *      cart-add goes through formula submit.
 */

(function () {
  'use strict';

  const SECTION_SELECTOR = '[data-yt-ingredient-pdp]';
  const BYO_PATH = '/pages/build-your-own';
  const BYO_STATE_SCHEMA_VERSION = 1;

  class IngredientPDPController {
    constructor(root) {
      this.root = root;
      this.slug = root.getAttribute('data-yt-slug') || '';
      this.appProxyBase = root.getAttribute('data-yt-app-proxy-base') || '/apps/yutrients';
      this.byoStateKey = root.getAttribute('data-yt-byo-state-key') || 'yutrients_byo_state';
      this.cacheKey = root.getAttribute('data-yt-catalogue-cache-key') || 'yutrients_catalogue_cache';
      this.cacheTtlMs = parseInt(root.getAttribute('data-yt-catalogue-cache-ttl-ms') || '300000', 10);

      // Hydrated by the catalogue fetch — needed for the add-to-blend
      // click handler since BYO sessionStorage stores ingredient_id +
      // amount_mg, both of which live on the catalogue row.
      this.ingredient = null;

      this.addBtn = root.querySelector('[data-yt-add-to-blend]');
      this.errorRegion = root.querySelector('[data-yt-add-error]');
      this.proseEls = Array.from(root.querySelectorAll('[data-yt-prose]'));
      this.contraindicationsEl = root.querySelector('[data-yt-contraindications]');
      this.safetyBlock = root.querySelector('[data-yt-safety-block]');
      this.relatedBlock = root.querySelector('[data-yt-related-block]');
      this.relatedList = root.querySelector('[data-yt-related-list]');
      this.relatedPrev = root.querySelector('[data-yt-related-prev]');
      this.relatedNext = root.querySelector('[data-yt-related-next]');

      this.bind();
      this.fetchCatalogueAndHydrate().catch((err) => {
        console.warn('[yt-ingredient-pdp] catalogue hydrate failed:', err);
        this.renderProseFallback();
      });
    }

    bind() {
      if (this.addBtn) {
        this.addBtn.addEventListener('click', () => this.addToBlend());
      }
      if (this.relatedPrev) {
        this.relatedPrev.addEventListener('click', () => this.scrollRelated(-1));
      }
      if (this.relatedNext) {
        this.relatedNext.addEventListener('click', () => this.scrollRelated(1));
      }
    }

    // ─── catalogue fetch + cache ───────────────────────────────────

    async fetchCatalogueAndHydrate() {
      const catalogue = await this.loadCatalogue();
      const row = (catalogue.ingredients || []).find((i) => i.slug === this.slug);
      if (!row) {
        console.warn(`[yt-ingredient-pdp] no catalogue row for slug "${this.slug}"`);
        this.renderProseFallback();
        return;
      }
      this.ingredient = row;
      this.hydrateProse(row);
      this.hydrateContraindications(row);
      this.hydrateRelated(row, catalogue);
    }

    async loadCatalogue() {
      // Try the sessionStorage cache first (5min TTL).
      try {
        const raw = sessionStorage.getItem(this.cacheKey);
        if (raw) {
          const { savedAt, data } = JSON.parse(raw);
          if (savedAt && Date.now() - savedAt < this.cacheTtlMs) {
            return data;
          }
        }
      } catch (_) {
        // sessionStorage can throw in private browsing; just refetch.
      }

      const res = await fetch(`${this.appProxyBase}/catalogue`, {
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`catalogue fetch ${res.status}`);
      const data = await res.json();

      try {
        sessionStorage.setItem(
          this.cacheKey,
          JSON.stringify({ savedAt: Date.now(), data })
        );
      } catch (_) {}

      return data;
    }

    // ─── prose hydration ──────────────────────────────────────────

    hydrateProse(row) {
      const sections = {
        mechanism: this.pickStory(row, 'mechanism'),
        sourcing: this.pickStory(row, 'sourcing'),
        research: this.pickStory(row, 'research'),
        safety: this.pickStory(row, 'safety') || row.safety_notes || '',
      };

      this.proseEls.forEach((el) => {
        const key = el.getAttribute('data-yt-prose');
        const text = sections[key];
        if (text) {
          el.innerHTML = this.paragraphsToHtml(text);
        } else {
          el.innerHTML = '<p class="yt-ingredient-pdp__prose-placeholder">Details coming soon.</p>';
        }
      });
    }

    pickStory(row, key) {
      // story_paragraphs is a JSON column on the catalogue row. Could
      // be a string OR an object {mechanism, sourcing, research, safety}.
      // Handle both shapes defensively.
      const sp = row.story_paragraphs;
      if (!sp) return '';
      if (typeof sp === 'string') return key === 'mechanism' ? sp : '';
      return sp[key] || '';
    }

    /**
     * Split on blank lines and wrap each chunk in a <p>. Falls back to
     * single <p> when no blank lines present. Escapes HTML to keep
     * stray < or > out.
     */
    paragraphsToHtml(text) {
      const esc = (s) =>
        String(s).replace(/[&<>"']/g, (c) => ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        })[c]);
      const parts = String(text)
        .split(/\n\s*\n/)
        .map((p) => p.trim())
        .filter(Boolean);
      if (parts.length === 0) return '';
      return parts.map((p) => `<p>${esc(p).replace(/\n/g, '<br/>')}</p>`).join('');
    }

    hydrateContraindications(row) {
      if (!this.contraindicationsEl) return;
      const list = Array.isArray(row.contraindications) ? row.contraindications : [];
      if (list.length === 0) {
        this.contraindicationsEl.hidden = true;
        return;
      }
      this.contraindicationsEl.innerHTML = list
        .map((c) => `<li>${String(c).replace(/[<>]/g, '')}</li>`)
        .join('');
      this.contraindicationsEl.hidden = false;
    }

    renderProseFallback() {
      // When the catalogue can't be reached at all, leave placeholders
      // saying so rather than the loading text — clearer signal to the
      // customer (and to me reviewing the page).
      this.proseEls.forEach((el) => {
        el.innerHTML =
          '<p class="yt-ingredient-pdp__prose-placeholder">Couldn\'t load ingredient details — try refreshing the page.</p>';
      });
    }

    // ─── related ingredients ──────────────────────────────────────

    hydrateRelated(row, catalogue) {
      if (!this.relatedList) return;
      const ids = Array.isArray(row.related_ingredients) ? row.related_ingredients : [];
      if (ids.length === 0) {
        this.relatedBlock.hidden = true;
        return;
      }
      const byId = new Map((catalogue.ingredients || []).map((i) => [i.id, i]));
      const cards = ids
        .map((id) => byId.get(id))
        .filter(Boolean)
        .slice(0, 8) // cap for safety
        .map((ing) => this.renderRelatedCard(ing))
        .join('');

      if (!cards) {
        this.relatedBlock.hidden = true;
        return;
      }
      this.relatedList.innerHTML = cards;
      this.relatedBlock.hidden = false;
    }

    renderRelatedCard(ing) {
      // The ingredient powder asset follows the same naming convention as
      // the BYO ingredient grid: assets/ingredient-<slug>.jpg
      const photoUrl = window.Shopify
        ? `/cdn/shop/files/ingredient-${ing.slug}.jpg`
        : '';
      const tagline = (ing.tagline || '').replace(/[<>]/g, '');
      const name = (ing.name || '').replace(/[<>]/g, '');
      return `
        <a class="yt-ingredient-pdp__related-card" href="/products/${encodeURIComponent(ing.slug)}">
          <div class="yt-ingredient-pdp__related-card-photo" style="background-image: url('${photoUrl}');"></div>
          <div class="yt-ingredient-pdp__related-card-body">
            <div class="yt-ingredient-pdp__related-card-name">${name}</div>
            <div class="yt-ingredient-pdp__related-card-sub">${tagline}</div>
          </div>
        </a>
      `;
    }

    scrollRelated(direction) {
      if (!this.relatedList) return;
      const cardWidth = this.relatedList.scrollWidth / Math.max(1, this.relatedList.children.length);
      this.relatedList.scrollBy({ left: cardWidth * direction, behavior: 'smooth' });
    }

    // ─── add to blend → mutate BYO sessionStorage + redirect ──────

    async addToBlend() {
      if (!this.ingredient) {
        // Catalogue hasn't loaded yet — try a quick reload so we don't
        // navigate the customer to a blank BYO state. Worst case they
        // wait ~200ms more.
        try {
          await this.fetchCatalogueAndHydrate();
        } catch (_) {}
        if (!this.ingredient) {
          this.showError("Couldn't load ingredient — please refresh the page.");
          return;
        }
      }

      const state = this.readByoState();
      const ingId = this.ingredient.id;
      const defaultDose = Number(this.ingredient.default_dose_mg) || 0;

      const already = (state.ingredients || []).find((i) => i.ingredient_id === ingId);
      if (!already) {
        state.ingredients = state.ingredients || [];
        state.ingredients.push({ ingredient_id: ingId, amount_mg: defaultDose });
      }
      state.schema_version = BYO_STATE_SCHEMA_VERSION;
      state.saved_at = new Date().toISOString();

      this.writeByoState(state);

      // Redirect to BYO. The BYO controller reads sessionStorage on
      // load (TTL 7 days) and pre-hydrates the picker.
      window.location.assign(BYO_PATH);
    }

    readByoState() {
      try {
        const raw = sessionStorage.getItem(this.byoStateKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') return parsed;
        }
      } catch (_) {}
      return {
        schema_version: BYO_STATE_SCHEMA_VERSION,
        stack_name: null,
        ingredients: [],
        primary_goals: [],
        pre_fill_hash: null,
        formula_id_prefill: null,
        saved_at: null,
      };
    }

    writeByoState(state) {
      try {
        sessionStorage.setItem(this.byoStateKey, JSON.stringify(state));
      } catch (err) {
        this.showError("Couldn't save to your blend — sessionStorage is unavailable.");
        throw err;
      }
    }

    showError(message) {
      if (!this.errorRegion) return;
      this.errorRegion.textContent = message;
      this.errorRegion.hidden = false;
    }
  }

  function init() {
    const root = document.querySelector(SECTION_SELECTOR);
    if (!root) return;
    new IngredientPDPController(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
